import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Page } from "../_shared";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Database, Trash2, Search } from "lucide-react";
import { toast } from "sonner";

export default function AdminDatabase() {
  const [collections, setCollections] = useState([]);
  const [active, setActive] = useState(null);
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [skip, setSkip] = useState(0);
  const limit = 25;

  useEffect(() => {
    api.get("/admin/db/collections").then((r) => {
      setCollections(r.data);
      if (r.data.length && !active) setActive(r.data[0].name);
    });
  }, []); // eslint-disable-line

  const load = () => {
    if (!active) return;
    const params = { skip, limit };
    if (q) params.q = q;
    api.get(`/admin/db/${active}`, { params }).then((r) => setData(r.data));
  };
  useEffect(() => { setData(null); setSkip(0); load(); }, [active]); // eslint-disable-line
  useEffect(() => { load(); }, [skip]); // eslint-disable-line

  const remove = async (id) => {
    if (!window.confirm(`Excluir documento ${id}?`)) return;
    try {
      await api.delete(`/admin/db/${active}/${id}`);
      toast.success("Removido");
      load();
      api.get("/admin/db/collections").then((r) => setCollections(r.data));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro");
    }
  };

  const keys = data && data.items.length > 0 ? Object.keys(data.items[0]).slice(0, 6) : [];

  return (
    <Page title="Banco de dados" subtitle="Visualize e gerencie coleções MongoDB diretamente.">
      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6">
        <aside className="space-y-1">
          <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2 px-2">Coleções</div>
          {collections.map((c) => (
            <button
              key={c.name}
              data-testid={`collection-${c.name}`}
              onClick={() => setActive(c.name)}
              className={`w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-lg text-sm transition-all ${
                active === c.name
                  ? "bg-primary/15 text-primary border border-primary/30"
                  : "text-muted-foreground hover:text-foreground hover:bg-accent/50 border border-transparent"
              }`}
            >
              <span className="flex items-center gap-2"><Database className="w-3.5 h-3.5" /> {c.name.replace("demandai_", "")}</span>
              <span className="text-xs font-mono">{c.count}</span>
            </button>
          ))}
        </aside>

        <div>
          <div className="flex gap-2 mb-4">
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input data-testid="db-search-input" placeholder="Buscar por id, email, nome, sku..." value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (setSkip(0), load())} className="pl-9" />
            </div>
            <Button data-testid="db-search-btn" onClick={() => { setSkip(0); load(); }}>Buscar</Button>
          </div>

          <div className="rounded-xl border border-border bg-card overflow-hidden">
            {!data ? (
              <div className="p-6 space-y-2">{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-10" />)}</div>
            ) : data.items.length === 0 ? (
              <div className="p-10 text-center text-sm text-muted-foreground">Nenhum documento.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border">
                      {keys.map((k) => <th key={k} className="text-left px-4 py-3 text-xs uppercase tracking-wider text-muted-foreground">{k}</th>)}
                      <th className="px-4"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.items.map((doc, i) => (
                      <tr key={doc.id || i} data-testid={`db-row-${doc.id || i}`} className="border-b border-border last:border-0 hover:bg-accent/30">
                        {keys.map((k) => (
                          <td key={k} className="px-4 py-3 max-w-xs truncate font-mono text-xs">
                            {typeof doc[k] === "object" ? JSON.stringify(doc[k]).slice(0, 60) : String(doc[k] ?? "—").slice(0, 60)}
                          </td>
                        ))}
                        <td className="px-4 text-right">
                          {doc.id && active !== "demandai_users" && (
                            <Button data-testid={`db-delete-${doc.id}`} variant="ghost" size="icon" onClick={() => remove(doc.id)}>
                              <Trash2 className="w-4 h-4 text-muted-foreground hover:text-red-400" />
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {data && data.total > limit && (
            <div className="flex items-center justify-between mt-4 text-sm">
              <Button variant="outline" disabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - limit))}>Anterior</Button>
              <span className="text-muted-foreground">{skip + 1}–{Math.min(skip + limit, data.total)} de {data.total}</span>
              <Button variant="outline" disabled={skip + limit >= data.total} onClick={() => setSkip(skip + limit)}>Próximo</Button>
            </div>
          )}
        </div>
      </div>
    </Page>
  );
}
